import React, { useEffect } from 'react';
import drawState from './enums/drawState';
import { Redirect } from "react-router-dom";
import { PrevSettings, PrevSettingsContent, PrevSettingsItem } from './components/prevSettings';
import Pages from './enums/pages';
import { AsideLayout } from './components/asideLayout';
import { obstaclesAdded, obstaclesMapReseted, obstaclesRemoved } from './action';
import DefineAreaPrecisionModal from './modals/defineAreaPrecisionInfo';
import InputMap1 from './partials/input-map1';
import { MapButton } from './components/mapButton';
import Alert from './components/alert';
import ReactGA from "react-ga4";

const initialPrecision = 50;
const maxDrawZoom = 17;

export function Obstacles(props) {
  const [fields, setFields] =
    React.useState(
      {
        "captcha": '',
        "areObstaclesDataCompleted": getAreObstaclesDataCompleted(),
        "obstaclesCount": props.store.getState()?.obstacles?.obstacles?.length ?? 0,
        "precision": initialPrecision,
        "start" : props.store.getState()?.obstacles?.start,
        "end" : props.store.getState()?.obstacles?.end,
        "areaPolygon": props.store.getState()?.obstacles?.areaPolygon
      });

  const [errors, setErrors] = React.useState({ captcha: '' });
  const [actualDrawState, setActualDrawState] = React.useState(drawState.MOVEMAP);
  const [redirect, setRedirect] = React.useState(areRoverDataComplete() ? Pages.Obstacles : Pages.Rover);
  const [showDefineAreaPrecisionModal, setShowDefineAreaPrecisionModal] = React.useState(false);
  const [isZoomedToDraw, setIsZoomedToDraw] = React.useState(false);
  const [hideZoomedToDrawWarning, setHideZoomedToDrawWarning] = React.useState(false);
  const [captchaImg, setCaptchaImg] = React.useState(null);
  const [drawScenario] = React.useState(createDrawScenarioWithReduxAction);
  const [removeScenario] = React.useState(createRemoveScenarioWithReduxAction);

  useEffect(() => {
    loadImage();
    setActualDrawState(getInitialDrawState());
    const unsubscribe = subscribeStateToReduxStore();

    if (!areObstaclesDataAlreadySet())
      initReduxStore(props.store);
    document.title = "Obstacles | Route Planning";
    return () => {
      unsubscribe();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page:window.location.pathname  });
    }, []);

  function initReduxStore(store) {
    store.dispatch(obstaclesAdded({ precision: initialPrecision }));
    store.dispatch(obstaclesAdded({ captcha: '' }));
  }

  function areObstaclesDataAlreadySet() {
    return props.store.getState()?.obstacles && !isObjectEmpty(props.store.getState()?.obstacles) && props.store.getState()?.obstacles.precision;
  }

  function areRoverDataComplete() {
    let rover = props.store.getState().rover;
    return rover.length && rover.turningRadius && rover.wheelDistance;
  }

  function createDrawScenarioWithReduxAction() {
    let drawScenario = new Map();
    drawScenario.set(drawState.START, (point) => {
      return obstaclesAdded({ start: point })
    });
    drawScenario.set(drawState.END, (point) => {
      return obstaclesAdded({ end: point })
    });
    drawScenario.set(drawState.OBSTACLES, (coordinates) => {
      return setObstacleOrAreaPolygon(coordinates);
    });
    drawScenario.set(drawState.AREAPOLYGON, (coordinates) => {
      return obstaclesAdded({ areaPolygon: coordinates })
    });
    return drawScenario;
  }

  function createRemoveScenarioWithReduxAction() {
    let removeScenario = new Map();
    removeScenario.set(drawState.START, () => {
      return obstaclesAdded({ start: null })
    });
    removeScenario.set(drawState.END, () => {
      return obstaclesAdded({ end: null })
    });
    removeScenario.set(drawState.OBSTACLES, (id) => {
      return obstaclesRemoved(id)
    });
    removeScenario.set(drawState.AREAPOLYGON, () => {
      return obstaclesAdded({ areaPolygon: null })
    });
    return removeScenario;
  }

  function subscribeStateToReduxStore() {
    return props.store.subscribe(() => {
      let formFields = {};
      let obstacles = props.store.getState().obstacles;
      formFields["start"] = obstacles.start;
      formFields["end"] = obstacles.end;
      formFields["obstacles"] = obstacles.obstacles;
      formFields["areaPolygon"] = obstacles.areaPolygon;
      formFields["precision"] = obstacles.precision;
      formFields["captcha"] = obstacles.captcha;
      formFields["obstaclesCount"] = props.store.getState()?.obstacles?.obstacles?.length ?? 0;
      formFields["areObstaclesDataCompleted"] = getAreObstaclesDataCompleted();
      setFields(formFields);
    });
  }

  function getAreObstaclesDataCompleted() {
    let obstacles = props.store.getState().obstacles;
    return (obstacles.start && obstacles.end && obstacles.areaPolygon && Array.isArray(obstacles.obstacles) && obstacles.obstacles.length > 0);
  }

  if (redirect === Pages.Obstacles) {
    let positionOfRotationAxis = props.store.getState().rover.positionOfRotationAxis;
    let positionOfGnsReceiver = props.store.getState().rover.positionOfGnsReceiver;
    let routePlanningEnabled = fields["areObstaclesDataCompleted"] && !objectPropsHasValues(errors);
    let precision = fields["precision"] ? fields["precision"] : initialPrecision;

    return <>
      {/* [BLOCK] Map */}
      <div className="interactive-block">
        <div className="map-toolbar">
          <MapButton isActive={actualDrawState === drawState.MOVEMAP} onClick={() => setActualDrawState(drawState.MOVEMAP)}>
            <i className='icon icon-hand-paper'></i>
          </MapButton>
          <div className="btn-group">
            <MapButton isActive={actualDrawState === drawState.START} onClick={() => setActualDrawState(drawState.START)} disabled={!isZoomedToDraw}>
              <img className='img-icon mr-2' src='./icon-start.png' alt='Start' /> Start
            </MapButton>
            <MapButton isActive={actualDrawState === drawState.END} onClick={() => setActualDrawState(drawState.END)} disabled={!isZoomedToDraw}>
              <img className='img-icon mr-2' src='./icon-end.png' alt='End' /> End
            </MapButton>
          </div>
          <MapButton isActive={actualDrawState === drawState.OBSTACLES} onClick={() => setActualDrawState(drawState.OBSTACLES)} disabled={!isZoomedToDraw}>
            <i className='icon icon-vector-square mr-2'></i> Obstacles
          </MapButton>
          <MapButton isActive={actualDrawState === drawState.AREAPOLYGON} onClick={() => setActualDrawState(drawState.AREAPOLYGON)} disabled={!isZoomedToDraw}>
            <i className='icon icon-vector-square mr-2'></i> Area
          </MapButton>
          <MapButton isActive={false} onClick={(e) => { props.store.dispatch(obstaclesMapReseted()); }} className='btn-danger'>
            <i className='icon icon-trash-alt mr-2'></i> Clear map
          </MapButton>
        </div>
        <InputMap1 drawStateHandler={drawStateHandler} store={props.store} areaPolygon={props.store.getState().obstacles.areaPolygon} drawState={actualDrawState}
          start={props.store.getState().obstacles.start} end={props.store.getState().obstacles.end} polygons={props.store.getState()?.obstacles?.obstacles}
          drawScenario={drawScenario} getFeatureIdBystate={getFeatureIdBystate} removeScenario={removeScenario}
          getPreventUnorientedPoint={getPreventUnorientedPoint} onZoomChanged={onZoomChanged}
          afterFeatureAdded={afterFeatureAdded}
          maxDrawZoom={maxDrawZoom}
        />
        <PrevSettings>
          <PrevSettingsContent header={"Rover"} link={Pages.Rover}>
            <PrevSettingsItem name={"Wheel Distance"} value={props.store.getState().rover.wheelDistance} unit={"m"} />
            <PrevSettingsItem name={"Length"} value={props.store.getState().rover.length} unit={"m"} />
            <PrevSettingsItem name={"Turning radius"} value={props.store.getState().rover.turningRadius} unit={"m"} />
            {positionOfRotationAxis && <PrevSettingsItem name={"Position of axis of rotation"} value={positionOfRotationAxis} unit={"m"} />}
            {positionOfGnsReceiver && <PrevSettingsItem name={"Position of GNSS receiver"} value={positionOfGnsReceiver} unit={"m"} />}
          </PrevSettingsContent>
        </PrevSettings>
      </div>
      <AsideLayout header={"Define obstacles"} >
        <div className="mb-6">
          <h2 className="h4-font-size subtitle">1) Define start and end point</h2>
          <div id="startPointMarker" className={`map-step ${isFeatureDone(drawState.START)}`}>
            <i className="icon icon-check pr-2 mb-2" />
            <span>Start point</span>
          </div>
          <div id="endPointMarker" className={`map-step mb-2 ${isFeatureDone(drawState.END)}`}>
            <i className="icon icon-check pr-2" />
            <span>End point</span>
          </div>
        </div>
        <div className="mb-6">
          <h2 className="h4-font-size subtitle">2) Draw your obstacles</h2>
          <div className="map-step info">
            <span id="obstaclesCount" className="pr-2">{fields["obstaclesCount"]}</span>
            <span>Obstacles</span>
          </div>
        </div>
        <div className="mb-7">
          <div className="d-flex justify-content-between align-items-baseline">
            <h2 className="h4-font-size subtitle">3) Define fence and precision</h2>
            <button type="button" className="btn btn-blank h4-font-size text-fade-color" data-toggle="modal"
              onClick={(e) => { setShowDefineAreaPrecisionModal(true) }}>
              <i className="icon icon-info-circle" />
            </button>
          </div>
          <div id="areaPointMarker" className={`map-step mb-2 ${isFeatureDone(drawState.AREAPOLYGON)}`}>
            <i className="icon icon-check pr-2" />
            <span>Fence</span>
          </div>
          <div className="form-group">
            <label htmlFor="precision">Precision</label>
            <input type="range" className="form-control-range" name="precision" value={precision} min={0} max={100}
              onChange={
                (e) => {
                  props.store.dispatch(obstaclesAdded({ precision: e.target.value }))
                }
              }
            />
            <div className="range-aprox-values">
              <span>Low</span>
              <span className="label text-fade-color">(<output id="precisionValue">{precision}</output>)</span>
              <span>High</span>
            </div>
          </div>
        </div>
        {captchaImg &&
          <div className="captcha-container">
            <div className="captcha-image">
              <img id="img-captcha" title="Can't read? Click to change another image." aria-label="Can't read? Click to change another image." src={captchaImg} onClick={() => loadImage()} />
            </div>
            <div className="captcha-input">
              <label htmlFor="captcha-input" className='required'>Captcha</label>
              <div className="form-group font-size-lg">
                <input type="text" id='captcha-input' className="form-control mb-1" placeholder="Enter captcha code" maxLength={4}
                  value={fields["captcha"]} error={errors["captcha"]}
                  onChange={(e) => props.store.dispatch(obstaclesAdded({ captcha: e.target.value }))}
                  onKeyPress={handleCaptchaKeypress}
                />
              </div>
              {errors["captcha"] && <span class="invalid-feedback  input-validation-error">{errors["captcha"]}</span>}
            </div>
          </div>
        }
        <button type="button" className={`btn w-100 mx-0 ${!routePlanningEnabled ? "disabled" : ""}`}
          onClick={async () => { await generateRoute(routePlanningEnabled); }}
        >Generate route
        </button>
      </AsideLayout>
      <div className='alerts-container'>
        {/*this.state.error && <Alert onClick={(e) => { setActualDrawState({ error: null }); }} variant='danger'>{this.state.error}</Alert>*/}
        {!isZoomedToDraw
          && !hideZoomedToDrawWarning
          && <Alert onClick={(e) => { setHideZoomedToDrawWarning(true); }} variant='info'>"Your map view is too large. Please zoom in to draw your obstacles. (Obstacle area size can be up to 50 000 m2)"</Alert>}
      </div>
      <DefineAreaPrecisionModal show={showDefineAreaPrecisionModal} hideAction={() => { setShowDefineAreaPrecisionModal(false) }} />
    </>
  }
  else
    return <Redirect to={redirect} />

  async function generateRoute(routePlanningEnabled) {
    let captchaOk = await processCaptcha();
    if (captchaOk && routePlanningEnabled && handleValidation()) {
      setRedirect(Pages.ObstaclesResult);
    }
  }

  function handleCaptchaKeypress(e) {
    if (e.charCode === 13) {
      e.preventDefault();
      let routePlanningEnabled = fields["areObstaclesDataCompleted"] && !objectPropsHasValues(errors);
      generateRoute(routePlanningEnabled);
    }
  };

  function afterFeatureAdded() {
    setActualDrawState(drawState.MOVEMAP)
  }

  function onZoomChanged(val) {
    let isZoomedToDraw = val >= maxDrawZoom;
    setIsZoomedToDraw(isZoomedToDraw);
  }

  function handleValidation() {
    let errors = {};
    let formIsValid = true;

    if (captchaImg && (fields["captcha"] === null || fields["captcha"] === undefined)) {
      formIsValid = false;
      errors["captcha"] = "Cannot be empty";
    }

    setErrors(errors);
    return formIsValid;
  }

  async function processCaptcha() {
    if (captchaImg)
      return true;

    return (!await loadImage());
  }

  function getFeatureIdBystate(myDrawState) {
    let obstaclesReduxState = props.store.getState()?.obstacles;
    switch (myDrawState) {
      case drawState.START: return obstaclesReduxState?.start?.Id;
      case drawState.END: return obstaclesReduxState?.end?.Id;
      case drawState.AREAPOLYGON:
        return Array.isArray(obstaclesReduxState?.areaPolygon) && obstaclesReduxState?.areaPolygon?.length > 0 ? obstaclesReduxState?.areaPolygon[0]?.Id : null;
      default: return null;
    }
  }

  function drawStateHandler(drawState) {
    setActualDrawState(drawState);
  }

  function isFeatureDone(stepState) {
    return fields[stepState] ? "done" : "";
  }

  function setObstacleOrAreaPolygon(coordinates) {
    if (coordinates[0] && coordinates[0][0] && coordinates[0][0][0] !== coordinates[0][coordinates.length - 1][0] && coordinates[0][0][1] !== coordinates[0][coordinates.length - 1][1])
      coordinates[0].push(coordinates[0][0]);
    return obstaclesAdded({ obstacles: coordinates[0] });
  }

  function getPreventUnorientedPoint(myDrawState) {
    return myDrawState === drawState.START;
  }

  function getInitialDrawState() {
    /* let obstacles = props.store.getState().obstacles;
     if (obstacles.areaPolygon) return drawState.NONE;
     if (obstacles.obstacles && obstacles.obstacles.length > 0) return drawState.OBSTACLES;
     if (obstacles.end) return drawState.OBSTACLES;
     if (obstacles.start) return drawState.END;*/
    return drawState.MOVEMAP
  }

  async function loadImage() {
    let response = null;
    try {
      response = await fetch('/api/Home/GetCaptchaImage',
        {
          mode: 'cors',
          credentials: 'include'
        });
    }
    catch { }

    if (response.status === "204") return false;
    // console.log(response.text);
    let blob = await response.blob();
    setCaptchaImg(URL.createObjectURL(blob));
    return true;
  }

  function isObjectEmpty(obj) {
    return obj
      && Object.keys(obj).length === 0
      && Object.getPrototypeOf(obj) === Object.prototype
  }

  function objectPropsHasValues(obj) {
    for (const key in obj) {
      if (obj[key])
      return true;
    }
    return false;
  }  
};